<script setup>
  import { useRoute } from 'vue-router'

  import { computed, onMounted, ref, watch } from 'vue'

  import DevModeBranding from '@/features/devMode/DevModeBranding.vue'
  import { useEmbedStore } from '@/features/embed/useEmbed.js'
  import LayoutHeader from '@/features/layout/components/LayoutHeader.vue'
  import SidebarPanelDesktop from '@/features/layout/components/SidebarPanelDesktop.vue'
  import { useLayoutStore } from '@/features/layout/useLayoutStore.js'
  import { useClubRouter } from '@/features/router/useClubRouer.js'
  import { useBreakpoint } from '@/features/ui/hooks/useBreakpoint.js'

  const props = defineProps({
    hamburgerTitle: {
      type: String,
      required: false,
    },
    sidebarLinks: {
      type: Array,
      required: false,
    },
    showSidebar: {
      type: Boolean,
      required: false,
      default: true,
    },
    showHeader: {
      type: Boolean,
      required: false,
      default: true,
    },
  })
  const layoutStore = useLayoutStore()
  const { isMobile } = useBreakpoint()
  const route = useRoute()
  const layoutPadding = computed(() =>
    route.meta.layoutPadding === undefined ? true : route.meta.layoutPadding,
  )

  const embedStore = useEmbedStore()
  const isEmbedded = embedStore.isEmbedded

  const classObject = computed(() => ({
    'sidebar-opened': layoutStore.sidebarOpened,
    'layout-padding': !!layoutPadding.value,
    'show-sidebar': props.showSidebar,
    'is-embedded': isEmbedded,
  }))

  onMounted(() => {
    layoutStore.setHamburgerTitle(props.hamburgerTitle)
    layoutStore.setSidebarMenuItems(props.sidebarLinks)
  })

  watch(
    () => props.sidebarLinks,
    value => {
      layoutStore.setSidebarMenuItems(value)
    },
    { deep: true },
  )

  const router = useClubRouter()

  const initialBackUrl = ref(router.options.history?.state?.back)

  const backUrl = computed(() => {
    const _backUrl = initialBackUrl.value

    // it can't go back!
    if (_backUrl?.startsWith('/embed')) {
      return null
    }

    return _backUrl
  })
  const isMailerPage = computed(() => {
    return route.path.startsWith('/admin/mailer')
  })

  const isSiteBuilderPage = computed(() => {
    return (
      route.path.includes('/page-builder/') ||
      route.path.includes('/sitemap-builder') ||
      route.path.includes('/content-importer')
    )
  })

  const isSitePreviewPage = computed(() => {
    const preview = Array.isArray(route.query.preview)
      ? route.query.preview[0]
      : route.query.preview
    return route.path.startsWith('/admin/site/') && preview === '1'
  })

  const hideLayoutHeader = computed(
    () =>
      route.matched.some(routeRecord => routeRecord.meta.hideLayoutHeader) ||
      isSitePreviewPage.value,
  )

  const showLayoutHeader = computed(() => props.showHeader && !hideLayoutHeader.value)

  const contentPanelMarginTop = computed(() => {
    if (!showLayoutHeader.value) {
      return '0'
    }

    return '60px'
  })

  const canGoBack = computed(() => {
    if (isMailerPage.value || isSiteBuilderPage.value) {
      return false
    }

    return !!backUrl.value
  })

  function handleClickBack() {
    if (canGoBack.value) {
      router.go(-1)
    }
  }

  router.afterEach(() => {
    const currentState = window.history.state
    initialBackUrl.value = currentState?.back || null
  })
</script>

<template>
  <template v-if="isEmbedded">
    <div v-if="canGoBack" class="embed-mode-top">
      <Button
        label="Go back"
        icon="pi pi-arrow-left"
        :data-go-back-url="backUrl"
        @click="handleClickBack"
      />
    </div>

    <slot name="router-view-top" />
    <slot></slot>
    <RouterView></RouterView>
  </template>

  <div v-else class="main-layout" :class="classObject">
    <DevModeBranding />
    <SidebarPanelDesktop v-if="!isMobile && showSidebar" />
    <div class="content-panel">
      <LayoutHeader v-if="showLayoutHeader" />
      <div class="content-panel-container" :style="{ marginTop: contentPanelMarginTop }">
        <slot name="router-view-top" />
        <slot></slot>
        <RouterView></RouterView>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .main-layout {
    --fm-ui-sidebar-width: 60px;
    --fm-ui-header-height: 60px;
    --fm-ui-sidebar-z-index: 1000;
    --fm-ui-content-panel-container-padding: 0;

    position: relative;
    min-height: 100dvh;
  }

  .layout-padding {
    --fm-ui-content-panel-container-padding: 16px;
  }

  .show-sidebar {
    --fm-ui-content-panel-margin-left: 60px;

    @include breakpoint(mobile) {
      --fm-ui-content-panel-margin-left: 0;
    }
  }

  .content-panel {
    position: relative;
    margin-left: var(--fm-ui-content-panel-margin-left);
    transition: all ease-in 0.2s;
    display: flex;
    flex-direction: column;
    min-height: 100dvh;
  }

  .content-panel-container {
    padding: var(--fm-ui-content-panel-container-padding);
    height: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    padding-bottom: 20px;
  }

  .sidebar-opened.show-sidebar {
    --fm-ui-sidebar-width: 250px;

    .content-panel {
      --fm-ui-content-panel-margin-left: 250px;
    }

    .layout-header {
      left: var(--fm-ui-content-panel-margin-left);
    }
  }

  @include breakpoint(mobile) {
    .main-layout {
      .content-panel {
        margin-left: 0;
      }
    }

    .sidebar-opened.show-sidebar {
      .layout-header {
        left: 0;
      }
    }
  }

  .embed-mode-top {
    margin-bottom: 15px;
  }
</style>
